import api from '@/services/ApiService';
import {
  Business,
  Close,
  Contacts,
  SupervisedUserCircleOutlined,
} from '@mui/icons-material';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  darken,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useState, useRef, useEffect } from 'react';
import qs from 'qs';

const PAGE_SIZE = 20;

/**
 * Party Selector Component
 * A unified dropdown to select ORGANIZATIONs or PERSONs
 * @param {*} param0
 * @returns
 */
export default function PartySelector({
  partyType,
  customer,
  supplier,
  party,
  setParty,
  endIcon,
  customerBlocked = false,
  disabled = false,
  description = '',
  filterIds = null,
}) {
  const theme = useTheme();
  const [edit, setEdit] = useState(false);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentQuery, setCurrentQuery] = useState('');
  const [page, setPage] = useState(0);
  const listboxRef = useRef(null);

  let type = 'Kontakte';
  if (partyType === 'ORGANIZATION') {
    type = 'Organisationen';
    if (customer && !supplier) {
      type = 'Firmenkunden';
    } else if (!customer && supplier) {
      type = 'Firmenlieferanten';
    }
  } else if (partyType === 'PERSON') {
    type = 'Personen';
    if (customer && !supplier) {
      type = 'Privatkunden';
    } else if (!customer && supplier) {
      type = 'Privatlieferanten';
    }
  } else {
    if (customer && !supplier) {
      type = 'Kunden';
    } else if (!customer && supplier) {
      type = 'Lieferanten';
    }
  }

  const title = party
    ? party.partyType === 'ORGANIZATION'
      ? party.company
      : `${party.firstName} ${party.lastName}`
    : description || `Alle ${type}`;
  const subtitle = party
    ? party.customerNumber || party.supplierNumber || party.email
    : 'Klicken zum Filtern';

  async function fetchParties(query, pageNum, append = false) {
    if (pageNum === 0) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    const skip = pageNum * PAGE_SIZE;
    const hasQuery = query && query.trim();

    if (filterIds && Array.isArray(filterIds) && filterIds.length > 0) {
      const results = await api.get(
        '/party?' +
          qs.stringify({
            _id: { $in: filterIds },
            ...(hasQuery && {
              $or: [
                { company: { $regex: query, $options: 'i' } },
                { firstName: { $regex: query, $options: 'i' } },
                { lastName: { $regex: query, $options: 'i' } },
                { email: { $regex: query, $options: 'i' } },
              ],
            }),
            skip,
            limit: PAGE_SIZE,
          }),
      );
      const newParties = results.data?.result || [];
      setParties((prev) => (append ? [...prev, ...newParties] : newParties));
      setHasMore(newParties.length === PAGE_SIZE);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    if (!hasQuery) {
      const filterParams = {
        ...(partyType && { partyType }),
        ...(customer && { customer: true }),
        ...(supplier && { supplier: true }),
        ...(customerBlocked === true && { customerBlocked: true }),
        ...(customerBlocked === false && { customerBlocked: { $ne: true } }),
        skip,
        limit: PAGE_SIZE,
      };

      const results = await api.get('/party?' + qs.stringify(filterParams));
      const newParties = results.data?.result || [];
      setParties((prev) => (append ? [...prev, ...newParties] : newParties));
      setHasMore(newParties.length === PAGE_SIZE);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    let customerBlockedFilter = null;

    if (customerBlocked === true) {
      customerBlockedFilter = {
        equals: {
          path: 'customerBlocked',
          value: true,
        },
      };
    } else if (customerBlocked === false) {
      customerBlockedFilter = {
        compound: {
          mustNot: [
            {
              equals: {
                path: 'customerBlocked',
                value: true,
              },
            },
          ],
        },
      };
    }

    const wildcardQuery =
      query.trim().replace(/[+\-&|!(){}[\]^"~*?:\\]/g, '\\$&') + '*';

    const results = await api.get(
      '/party?' +
        qs.stringify({
          search: {
            compound: {
              filter: [
                partyType && {
                  text: {
                    path: 'partyType',
                    query: partyType,
                  },
                },
                customer && {
                  equals: {
                    path: 'customer',
                    value: true,
                  },
                },
                supplier && {
                  equals: {
                    path: 'supplier',
                    value: true,
                  },
                },
                customerBlockedFilter,
              ].filter(Boolean),
              should: [
                {
                  text: {
                    query,
                    path: 'company',
                    fuzzy: { maxEdits: 1 },
                  },
                },
                {
                  text: {
                    query,
                    path: 'firstName',
                    fuzzy: { maxEdits: 1 },
                  },
                },
                {
                  text: {
                    query,
                    path: 'lastName',
                    fuzzy: { maxEdits: 1 },
                  },
                },
                {
                  queryString: {
                    defaultPath: 'company',
                    query: wildcardQuery,
                  },
                },
                {
                  queryString: {
                    defaultPath: 'firstName',
                    query: wildcardQuery,
                  },
                },
                {
                  queryString: {
                    defaultPath: 'lastName',
                    query: wildcardQuery,
                  },
                },
                {
                  queryString: {
                    defaultPath: 'email',
                    query: wildcardQuery,
                  },
                },
                {
                  text: {
                    query,
                    path: 'customerNumber',
                  },
                },
                {
                  text: {
                    query,
                    path: 'supplierNumber',
                  },
                },
                {
                  text: {
                    query,
                    path: 'email',
                    fuzzy: { maxEdits: 1 },
                  },
                },
              ],
              minimumShouldMatch: 1,
            },
          },
          skip,
          limit: PAGE_SIZE,
        }),
    );
    const newParties = results.data?.result || [];
    setParties((prev) => (append ? [...prev, ...newParties] : newParties));
    setHasMore(newParties.length === PAGE_SIZE);
    setLoading(false);
    setLoadingMore(false);
  }

  // Initial load when opening
  const loadInitial = useCallback(() => {
    setParties([]);
    setPage(0);
    setHasMore(true);
    setCurrentQuery('');
    fetchParties('', 0, false);
  }, [filterIds, partyType, customer, supplier, customerBlocked]);

  // Search with debounce
  const handleSearch = useCallback(
    debounce((query) => {
      setParties([]);
      setPage(0);
      setHasMore(true);
      setCurrentQuery(query);
      fetchParties(query, 0, false);
    }, 300),
    [filterIds, partyType, customer, supplier, customerBlocked],
  );

  // Load more on scroll
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchParties(currentQuery, nextPage, true);
  }, [
    page,
    currentQuery,
    loadingMore,
    hasMore,
    filterIds,
    partyType,
    customer,
    supplier,
    customerBlocked,
  ]);

  // Handle listbox scroll
  const handleListboxScroll = useCallback(
    (event) => {
      const listbox = event.target;
      if (
        listbox.scrollTop + listbox.clientHeight >=
        listbox.scrollHeight - 50
      ) {
        loadMore();
      }
    },
    [loadMore],
  );

  // Load initial data when edit mode is enabled
  useEffect(() => {
    if (edit) {
      loadInitial();
    }
  }, [edit, loadInitial]);

  const iconPartyType = party ? party.partyType : partyType;

  return (
    <Card
      sx={{
        p: 2,
        width: '100%',
        height: '60px',
        boxShadow: 'none',
        backgroundColor: darken(theme.palette.background.paper, 0.03),
        cursor: disabled ? 'not-allowed' : !edit && 'pointer',
        userSelect: 'none',
        display: 'flex',
        flexDirection: 'row',
        gap: 1.5,
        alignItems: 'center',
        opacity: disabled ? 0.6 : 1,
        '&:hover': {
          backgroundColor:
            !edit && !disabled && darken(theme.palette.background.paper, 0.06),
        },
        '&:active': {
          backgroundColor:
            !edit && !disabled && darken(theme.palette.background.paper, 0.09),
        },
        overflow: 'hidden',
      }}
      onClick={() => !edit && !disabled && setEdit(true)}
    >
      {!edit && (
        <>
          <Avatar
            // src={party?.logo}
            sx={{
              width: 35,
              height: 35,
              '& img': { objectFit: 'contain', width: '80%' },
              backgroundColor: theme.palette.background.default,
            }}
          >
            {iconPartyType === 'ORGANIZATION' && (
              <Business
                sx={{ fontSize: 18, color: theme.palette.primary.main }}
              />
            )}
            {iconPartyType === 'PERSON' && (
              <SupervisedUserCircleOutlined
                sx={{ fontSize: 18, color: theme.palette.primary.main }}
              />
            )}
            {!iconPartyType && (
              <Contacts
                sx={{ fontSize: 18, color: theme.palette.primary.main }}
              />
            )}
          </Avatar>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: 'subtitle1.fontSize',
                lineHeight: 1.2,
                maxWidth: '90%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={title}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.2,
                maxWidth: '90%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={subtitle}
            >
              {subtitle}
            </Typography>
          </Box>
          {endIcon && endIcon}
          {party && (
            <Button
              onClick={(evt) => {
                evt.stopPropagation();
                setParty(null);
              }}
              size="small"
              sx={{
                minWidth: '30px',
                width: '30px',
                height: '30px',
                padding: 0,
                fontSize: '16px',
              }}
            >
              <Close />
            </Button>
          )}
        </>
      )}
      {edit && (
        <Autocomplete
          open={edit}
          onOpen={() => setEdit(true)}
          noOptionsText={loading ? 'Laden...' : 'Keine Ergebnisse gefunden'}
          onClose={(event, reason) => {
            // prevent closing when clicking in the input
            if (['blur', 'escape', 'selectOption'].includes(reason))
              setEdit(false);
          }}
          onChange={(evt, value) => setParty(value)}
          options={parties || []}
          getOptionLabel={(option) =>
            option.partyType === 'ORGANIZATION'
              ? option.company
              : `${option.firstName} ${option.lastName}`
          }
          ListboxProps={{
            onScroll: handleListboxScroll,
            ref: listboxRef,
            style: { maxHeight: 300 },
          }}
          renderOption={(props, option, { index }) => (
            <ListItem {...props} key={option._id}>
              <ListItemText
                primary={
                  option.partyType === 'ORGANIZATION'
                    ? option.company
                    : `${option.firstName} ${option.lastName}`
                }
                secondary={
                  option.customerNumber || option.supplierNumber || option.email
                }
                sx={{
                  '& .MuiListItemText-primary': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiListItemText-secondary': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </ListItem>
          )}
          filterOptions={(x) => x}
          fullWidth
          loading={loading || loadingMore}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Kundenname"
              autoFocus
              sx={{
                '& .MuiInputBase-input': { p: 1.5 },
                '& .MuiOutlinedInput-root': { pr: '16px !important' },
              }}
              fullWidth
              onChange={(evt) => handleSearch(evt.target.value)}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading || loadingMore ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      )}
    </Card>
  );
}
